import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  actionOrder,
  cancelOrder,
  getNewOrderEvents,
  getOrderPrintDetail,
  getOrdersHistory,
  getOrdersV2,
} from "../../api/order";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { dispatchOrdersCombo, getDrivers } from "../../api/driver";
import { RESET } from "../types";
import { getProductQuantityNumberOverUnit } from "../../extras";
import { cancelIfoodOrderRequest, postShippingOrder } from "../../api/shipping";
import { dispatchAddAlert } from "../../utils/alerts";
import { setShowShippingRouteModal } from "./shippingRoutesSlice";
import { printOrderTicket, setupQzTraySecurity } from "../../utils/qzTray";
import qz from "qz-tray";
import { addToQueue } from "./orderQueueSlice";
import { createTransform } from "redux-persist";

export const getOrdersHistoryThunk = createAsyncThunk(
  "orders/getOrdersHistory",
  async ({ params, newSearch }, { rejectWithValue }) => {
    try {
      const { data } = await getOrdersHistory(params);

      return {
        orders: data,
        newSearch: newSearch,
        page: params.page,
        showButtonMore: data.length > 0,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const updateOrders = createAsyncThunk(
  "orders/updateOrders",
  async (
    {
      newSearch = true,
      isRefresh = false,
      includeNeighborhoodGroups = undefined,
    },
    { getState, dispatch }
  ) => {
    try {
      if (isRefresh) {
        dispatch(setLoadingOrders(true));
      }

      const state = getState().orders;
      const stateOrders = getState().orders.orders;

      if (!stateOrders) {
        return;
      }

      const page = newSearch ? 1 : state.page;

      const params = {
        page,
        include_neighborhood_groups: includeNeighborhoodGroups,
      };
      const { data } = await getOrdersV2(params);

      const newOrders = data.filter(
        ({ order_id }) => !stateOrders.ids.includes(order_id)
      );

      const showButtonMore = newOrders.length > 0;

      const updatedOrders = newSearch
        ? data
        : [
            ...stateOrders.ids.map((id) => stateOrders.entities[id]),
            ...newOrders,
          ];

      const updatedResult = updatedOrders.map((o) => {
        const found = data.find((no) => o.order_id === no.order_id);
        const status_number = found
          ? (o.status_number === 2 || o.status_number === 1) &&
            found.status_number === 0
            ? o.status_number
            : (o.status_number === 5 || o.status_number === 3) &&
              found.status_number === 2
            ? o.status_number
            : found.status_number
          : o.status_number;

        const expected = new Date(o.delivery_expected_time);
        const now = new Date();

        return {
          ...o,
          status_number: status_number,
          lateStatus: expected < now,
          isAlmostLate:
            new Date(o.delivery_expected_time) - new Date() < 10 * 60 * 1000 &&
            new Date(o.delivery_expected_time) > new Date(),
        };
      });

      // Remove orders with status_number greater than 4 from ordersToDispatch
      const ordersToDispatch = state.ordersToDispatch.filter((orderId) => {
        const order = state.orders.entities[orderId];
        return !order || order.status_number <= 4;
      });

      const shouldPlayAlertSound = updatedResult.some(
        (order) => order.status_number === 0
      );

      return {
        orders: updatedResult,
        page: page + 1,
        showButtonMore: showButtonMore,
        shouldPlayAlertSound: shouldPlayAlertSound,
        ordersToDispatch,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
);

export const updateStatusOrders = createAsyncThunk(
  "orders/updateOnWayOrders",
  // eslint-disable-next-line no-empty-pattern
  async ({ status = "orders-on-way" }, { getState, dispatch }) => {
    try {
      const stateOrders = getState().orders.statusOrders;

      dispatch(setLoadingOrders(true));

      if (!stateOrders) {
        return;
      }

      const params = {
        on_way: status === "orders-on-way" ? true : undefined,
        finished: status === "orders-completed" ? true : undefined,
      };
      const { data } = await getOrdersV2(params);

      data.sort(
        (a, b) => new Date(b.preparation_start) - new Date(a.preparation_start)
      );

      return {
        statusOrders: data,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
);

export const updatedOrders = createAsyncThunk(
  "orders/updatedOrders",
  async (_, { getState, dispatch }) => {
    try {
      const state = getState().orders;
      if (!state) return;

      // Fetch new orders and events from API
      const {
        data: { orders, events, should_print_auto_accept },
      } = await getNewOrderEvents();

      // Filter new orders not in state
      const newOrders = orders.filter(
        ({ order_id }) => !state.orders.ids.includes(order_id)
      );

      // Process updated orders and maintain status_number logic
      const updatedResult = state.orders.ids.map((id) => {
        const existingOrder = state.orders.entities[id];
        const fetchedOrder = orders.find(
          (no) => existingOrder.order_id === no.order_id
        );

        const status_number = fetchedOrder
          ? ([1, 2].includes(existingOrder.status_number) &&
              fetchedOrder.status_number === 0) ||
            ([3, 5].includes(existingOrder.status_number) &&
              fetchedOrder.status_number === 2)
            ? existingOrder.status_number
            : [1, 2].includes(existingOrder.status_number) &&
              fetchedOrder.status_number === 7
            ? existingOrder.status_number
            : fetchedOrder.status_number
          : existingOrder.status_number;

        return {
          ...existingOrder,
          status_number,
          lateStatus:
            new Date(existingOrder.delivery_expected_time) < new Date(),
          isAlmostLate:
            new Date(existingOrder.delivery_expected_time) - new Date() <
              10 * 60 * 1000 &&
            new Date(existingOrder.delivery_expected_time) > new Date(),
        };
      });

      updatedResult.sort((a, b) => {
        if (a.lateStatus && !b.lateStatus) return -1;
        if (!a.lateStatus && b.lateStatus) return 1;
        if (a.isAlmostLate && !b.isAlmostLate) return -1;
        if (!a.isAlmostLate && b.isAlmostLate) return 1;
        return a.preparation_start - b.preparation_start;
      });

      // Combine new and updated orders
      const updatedOrders = [...newOrders, ...updatedResult];

      // Identify orders to delete
      const shouldDelete = updatedOrders.filter(
        (order) => order.status_number > 2 && order.status_number !== 7
      );
      const shouldDeleteIds = shouldDelete.map((order) => order.order_id);

      // Filter out orders that should be deleted or have status_number >= 3
      let ordersToDispatch = state.ordersToDispatch.filter((orderId) => {
        const order = state.orders.entities[orderId];
        return (
          order &&
          !shouldDeleteIds.includes(orderId) &&
          (order.status_number < 3 || order.status_number === 7)
        );
      });

      if (ordersToDispatch.length === 0) {
        ordersToDispatch = [];
      }

      // Dispatch actions to update state
      dispatch(updateOrdersToDispatch(ordersToDispatch));
      dispatch(saveOrderEvents(events));

      // Handle auto-accept orders
      if (state.autoAcceptOrders && newOrders.length > 0) {
        newOrders.forEach((order) =>
          dispatch(addToQueue({ ...order, should_print_auto_accept }))
        );
      }

      return {
        updatedOrders,
        shouldPlayAlertSound: updatedOrders.some(
          (order) => order.status_number === 0
        ),
        should_print_auto_accept,
      };
    } catch (error) {
      console.error("Error updating orders:", error);
    }
  }
);

export const searchForOrder = createAsyncThunk(
  "orders/getOrder",
  async ({ query, alignment }, {}) => {
    try {
      const page = 1;

      const params = { page, query, get_all: true };
      const { data } = await getOrdersV2(params);

      return {
        orders: data,
        alignment: alignment,
      };
    } catch (error) {
      console.error(error);
    }
  }
);

export const acceptOrderThunk = createAsyncThunk(
  "orders/acceptOrder",
  async (
    { order, isAutoAccept = false, shouldPrint = false },
    { getState, dispatch }
  ) => {
    console.log("orderrrrrrrrrrrrrrr", order);
    const { channel, channel_order_id, order_id } = order;

    try {
      await actionOrder(channel, channel_order_id, "confirm");

      if (isAutoAccept && shouldPrint) {
        dispatch(fetchOrderPrintDetails({ orderId: order_id }));
      }

      // Return only the changes for the specific order
      return { id: order_id, changes: { status_number: 1 }, isAutoAccept };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        error: error.response?.data?.message || "An error occurred",
        id: order_id,
      };
    }
  }
);

export const readyToPickupOrderThunk = createAsyncThunk(
  "orders/readyToPickupOrder",
  async ({ order }, { getState }) => {
    const { channel, channel_order_id, order_id } = order;

    try {
      await actionOrder(channel, channel_order_id, "ready");

      const state = getState();
      const currentOrder = selectOrderById(state, order_id);

      if (!currentOrder) {
        console.error("Order not found on entities");
        return;
      }

      // Return only the changes for the specific order
      return { id: order_id, changes: { status_number: 21 } };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        error: error.response?.data?.message || "An error occurred",
        id: order_id,
      };
    }
  }
);

export const finishOrderThunk = createAsyncThunk(
  "orders/finishOrder",
  async ({ order }, { getState }) => {
    const { channel, channel_order_id, order_id } = order;

    try {
      await actionOrder(channel, channel_order_id, "finish");

      const state = getState();
      const currentOrder = selectOrderById(state, order_id);

      if (!currentOrder) {
        console.error("Order not found on entities");
        return;
      }

      // Return only the changes for the specific order
      return { id: order_id, changes: { status_number: 27 } };
    } catch (error) {
      dispatchAddAlert({
        message: error.response.data.message,
      });
      console.error(error);

      return {
        success: false,
        error: error.response?.data?.message || "An error occurred",
        id: order_id,
      };
    }
  }
);

export const packedOrderThunk = createAsyncThunk(
  "orders/packedOrder",
  async ({ order }, { getState }) => {
    const { channel, channel_order_id, order_id } = order;

    try {
      await actionOrder(channel, channel_order_id, "packed");

      const state = getState();
      const currentOrder = selectOrderById(state, order_id);

      if (!currentOrder) {
        console.error("Order not found on entities");
        return;
      }

      return { id: order_id, changes: { status_number: 7 } };
    } catch (error) {
      dispatchAddAlert({
        message: error.response.data.message,
      });
      console.error(error);
    }
  }
);

export const cancelOrderThunk = createAsyncThunk(
  "orders/cancelOrder",
  async ({ order, cancelData }, { getState, dispatch }) => {
    const { channel, channel_order_id, order_id } = order;
    const { code, reason } = cancelData;

    try {
      await cancelOrder(channel, channel_order_id, code, reason);

      const state = getState();
      const currentOrder = selectOrderById(state, order_id);

      if (!currentOrder) {
        console.error("Order not found on entities");
        return;
      }

      // Return only the changes for the specific order
      return { id: order_id, changes: { status_number: 55 } };
    } catch (error) {
      console.error(error);
    }
  }
);

export const cancelDriverRequestThunk = createAsyncThunk(
  "orders/cancelIfoodOrderDriverRequest",
  async ({ order_id }, { getState, dispatch }) => {
    try {
      const params = {
        order_id,
      };

      await cancelIfoodOrderRequest(params);

      const state = getState();
      const currentOrder = selectOrderById(state, order_id);

      if (!currentOrder) {
        console.error("Order not found on entities");
        return;
      }

      // Return only the changes for the specific order
      return { id: order_id, changes: { status_number: 2 } };
    } catch (error) {
      console.error(error);
    }
  }
);

export const postOrderDriverRequestThunk = createAsyncThunk(
  "orders/postOrderDriverRequest",
  async ({ mid, order_id, quote_id }, { getState, dispatch }) => {
    try {
      const params = {
        mid,
        order_id,
        quote_id,
      };

      await postShippingOrder(params);

      const state = getState();
      const currentOrder = selectOrderById(state, order_id);

      if (!currentOrder) {
        console.error("Order not found on entities");
        return;
      }

      // Return only the changes for the specific order
      return { id: order_id, changes: { status_number: 10 } };
    } catch (error) {
      console.error(error);
    }
  }
);

export const dispatchOrders = createAsyncThunk(
  "orders/dispatchOrders",
  async (
    { driverId, equipmentId },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const ordersToDispatch = state.orders.ordersToDispatch;

      if (!ordersToDispatch.length) {
        return rejectWithValue("Failed to dispatch orders");
      }

      // Dispatch orders combo API call
      const { data } = await dispatchOrdersCombo(
        driverId,
        ordersToDispatch,
        equipmentId
      );

      dispatch(
        setShowShippingRouteModal({
          show: true,
          shippingRoute: data,
        })
      );

      // Return necessary data for state update in `fulfilled`
      return {
        driverId,
        ordersToDispatch,
      };
    } catch (e) {
      console.error(e);
      // Preserve the backend payload (pt-BR message, error_code, quota details)
      // so the rejected reducer can surface the plan-limit modal instead of a
      // generic snackbar.
      return rejectWithValue(
        e?.response?.data || { error: "Failed to dispatch orders" }
      );
    }
  }
);

export const finishSaloonOrderThunk = createAsyncThunk(
  "orders/finishSaloonOrder",
  async ({ order }, { getState, dispatch }) => {
    const { channel, channel_order_id, order_id } = order;

    try {
      await actionOrder(channel, channel_order_id, "finish");
    } catch (error) {
      console.error("ERROR FINISH SALOON ORDER", error.response?.data?.message);

      const message = error.response?.data?.message || "Erro ao fechar mesa";
      dispatchSnackBarMessage(order_id, message, "error");
    }
  }
);

export const fetchOrderDetails = createAsyncThunk(
  "orders/fetchOrderDetails",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const params = { oid: orderId };

      const { data } = await getOrderPrintDetail(params);

      return data;
    } catch (err) {
      const errorData = err.response?.data || err.message;
      return rejectWithValue(errorData);
    }
  }
);

export const fetchOrderPrintDetails = createAsyncThunk(
  "orders/fetchOrderPrintDetails",
  async ({ orderId }, { rejectWithValue, dispatch }) => {
    try {
      const params = { oid: orderId };
      const { data } = await getOrderPrintDetail(params);

      const { products, complements, benefits, channel } = data;

      const productsArray = products.map((product) => ({
        product_id: product.channel_product_id,
        orderProduct: product.id,
        product_identifier: product.id,
        title: product.product_title,
        index: product.item_index,
        observations: product.observations,
        quantity: getProductQuantityNumberOverUnit(
          product.unit,
          product.quantity
        ),
        total_price: product.total_price,
        price: product.un_price,
        complementsPrice: product.options_price,
        product_option:
          product.product_option !== "Unidade" ? product.product_option : "",
        unit: product.unit,
        isMainProduct: true,
        level: 1,
        label: "strong",
      }));

      const complementsArray = complements.map((complement) => ({
        complement_id: complement.fk_complement,
        orderComplement: complement.id,
        complementOrderProduct: complement.fk_order_product,
        title: complement.fk_complement_title,
        index: complement.complement_index,
        observations: null,
        quantity: getProductQuantityNumberOverUnit(
          complement.unit,
          complement.quantity
        ),
        total_price: complement.total_price,
        price: complement.un_price,
        complement_categorie: complement.complement_categorie,
        unit: complement.unit,
        level: complement.complement_level,
        label: "sub",
      }));

      const allProducts = [...productsArray, ...complementsArray];
      allProducts.sort((a, b) => a.index - b.index);

      for (const product of products) {
        let mainQnt;
        if (product.label === "strong") {
          mainQnt = product.quantity;
        }
        if (product.label === "sub" && channel === "IFOOD") {
          product.quantity *= mainQnt;
        }
      }

      let driverData = null;
      if (data.status_number >= 5) {
        const driverResponse = await getDrivers({ did: data.fk_driver });
        driverData = driverResponse.data;
      }

      const orderDetail = {
        ...data,
        allProducts,
        benefits,
        driverData,
      };

      // Print the ticket
      try {
        await printOrderTicket({
          order: orderDetail,
          products: orderDetail.allProducts,
          benefits: orderDetail.benefits,
        });
      } catch (printError) {
        console.error("Failed to print order ticket:", printError);
        return rejectWithValue("Failed to print order ticket");
      }

      return orderDetail;
    } catch (err) {
      console.log("ERRRR", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const setAutoAcceptOrders = createAsyncThunk(
  "orders/setAutoAcceptOrders",
  async ({ value }, { rejectWithValue }) => {
    try {
      console.log("AAAAA");
      await setupQzTraySecurity();
      const isValid = qz.websocket.isActive();

      console.log("IS VALID", isValid);
      if (!isValid) {
        dispatchSnackBarMessage(
          `set-auto-accept-orders-rejected-${new Date()}`,
          'Verifique se o programa "Qz Tray está ligado em seu computador"',
          "error"
        );
        return rejectWithValue("QZ Tray not connected");
      }

      if (value) {
        dispatchAddAlert({
          message:
            "Seus pedidos serão aceitos e impressos automaticamento pelo Slapie, não precisa ativar isso em outro computador, só nesse já basta.",
        });
      }

      return value;
    } catch (error) {
      console.log(error);
      dispatchSnackBarMessage(
        `set-auto-accept-orders-rejected-${new Date()}`,
        'Verifique se o programa "Qz Tray está ligado em seu computador"',
        "error"
      );
      return rejectWithValue("QZ Tray not connected");
    }
  }
);

export const addCreatedOrders = createAsyncThunk(
  "orders/addCreatedOrders",
  async (payload, { getState, dispatch }) => {
    const state = getState().orders;
    const existingOrderIds = new Set(state.orders.ids);

    const normalizedData = normalizeOrdersPayload(payload);
    const { orders, shouldPrintAutoAccept } = normalizedData;

    const hasNoOrders = !orders || orders.length === 0;
    if (hasNoOrders) {
      return {
        orders: [],
        should_print_auto_accept: false,
      };
    }

    const newOrders = filterNewOrders(orders, existingOrderIds);

    console.log("existingOrderIds", existingOrderIds);
    console.log("filteredOrders", newOrders);

    return {
      orders: newOrders,
      should_print_auto_accept: shouldPrintAutoAccept,
      autoAcceptOrders: state.autoAcceptOrders,
    };
  }
);

function normalizeOrdersPayload(payload) {
  const isSingleOrderObject = payload.order_id !== undefined;

  if (isSingleOrderObject) {
    return {
      orders: [payload],
      shouldPrintAutoAccept: false,
    };
  }

  const ordersArray = Array.isArray(payload.orders) ? payload.orders : [];
  const shouldPrint = payload.should_print_auto_accept || false;

  return {
    orders: ordersArray,
    shouldPrintAutoAccept: shouldPrint,
  };
}

function filterNewOrders(orders, existingOrderIds) {
  return orders.filter((order) => {
    return !existingOrderIds.has(order.order_id);
  });
}

const ordersAdapter = createEntityAdapter({
  selectId: (order) => order?.order_id,
  sortComparer: (a, b) => {
    // Ensure orders with status_number === 0 are always at the top
    if (a.status_number === 0 && b.status_number !== 0) return -1;
    if (a.status_number !== 0 && b.status_number === 0) return 1;

    // First, check for lateStatus
    if (a.lateStatus && !b.lateStatus) return -1;
    if (!a.lateStatus && b.lateStatus) return 1;

    // If lateStatus is the same, check for isAlmostLate
    if (a.lateStatus === b.lateStatus) {
      if (a.isAlmostLate && !b.isAlmostLate) return -1;
      if (!a.isAlmostLate && b.isAlmostLate) return 1;
    }

    // If both lateStatus and isAlmostLate are the same, sort by preparation_start (earlier to later)
    return a.preparation_start - b.preparation_start;
  },
});

const statusOrdersAdapter = createEntityAdapter({
  selectId: (order) => order?.order_id,
});

const orderEventsAdapter = createEntityAdapter();

const initialState = {
  orders: ordersAdapter.getInitialState(),
  statusOrders: statusOrdersAdapter.getInitialState(),
  cancelledOrdersAlertCache: {},
  orderEvents: orderEventsAdapter.getInitialState(),
  page: 1,
  loadingOrders: false,
  showButtonMore: true,
  orderInfosModalOrderId: undefined,
  showOrderInfosModal: false,
  newOrderTable: undefined,
  showNewOrderModal: false,
  showResetCountModal: false,
  shouldPlayAlertSound: false,
  shouldPlayFirstOrderOfDayAlertSound: false,
  loadingOrderDetail: false,
  orderDetails: false,
  orderPrintDetails: false,
  showDispatchOrdersModal: false,
  showDispatchOrdersModalProviders: false,
  orderLimitModal: { open: false, message: null, details: null },
  ordersToDispatch: [],
  preparingOrdersCount: undefined,
  onWayOrdersCount: undefined,
  activeShippingRoutesCount: undefined,
  autoAcceptOrders: false,
  playOrderAlert: false,
};

// Transform to persist only the 'cancelledOrdersAlertCache'
export const cancelledOrdersTransform = createTransform(
  // Serialize state before persisting
  (inboundState) => ({
    cancelledOrdersAlertCache: inboundState.cancelledOrdersAlertCache,
  }),
  // Deserialize state when rehydrating
  (outboundState) => ({
    ...outboundState,
    orders: {}, // Reset other parts of the state to their defaults
    statusOrders: {},
    orderEvents: {},
    page: 1,
    loadingOrders: false,
    showButtonMore: true,
    orderInfosModalOrderId: undefined,
    showOrderInfosModal: false,
    newOrderTable: undefined,
    showResetCountModal: false,
    shouldPlayAlertSound: false,
    shouldPlayFirstOrderOfDayAlertSound: false,
    loadingOrderDetail: false,
    orderDetails: false,
    orderPrintDetails: false,
    showDispatchOrdersModal: false,
    ordersToDispatch: [],
    preparingOrdersCount: undefined,
    onWayOrdersCount: undefined,
    activeShippingRoutesCount: undefined,
    autoAcceptOrders: false,
  }),
  { whitelist: ["orders"] }
);

const calculateOrderStatus = (order) => {
  const currentTime = new Date();
  const deliveryTime = new Date(order.delivery_expected_time);

  // Calculate time difference in milliseconds
  const timeUntilDelivery = deliveryTime - currentTime;
  const tenMinutesInMs = 10 * 60 * 1000;

  return {
    ...order,
    lateStatus: deliveryTime < currentTime,
    isAlmostLate: timeUntilDelivery < tenMinutesInMs && timeUntilDelivery > 0,
  };
};

// Higher-level business logic next
const processOrders = (orders) => {
  return orders.map(calculateOrderStatus);
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    closeOrderLimitModal: (state) => {
      state.orderLimitModal = { open: false, message: null, details: null };
    },
    updateOrderStatus: (state, action) => {
      const { order_id, new_status } = action.payload;

      // Check if order should be removed (status > 2 && !== 7)
      const shouldRemove = new_status > 2 && new_status !== 7;

      // Update or remove in orders adapter
      const existingOrder = state.orders.entities[order_id];
      if (existingOrder) {
        if (shouldRemove) {
          ordersAdapter.removeOne(state.orders, order_id);
        } else {
          ordersAdapter.updateOne(state.orders, {
            id: order_id,
            changes: { status_number: new_status },
          });
        }
      }

      // Update or remove in statusOrders adapter
      const existingStatusOrder = state.statusOrders.entities[order_id];
      if (existingStatusOrder) {
        if (shouldRemove) {
          statusOrdersAdapter.removeOne(state.statusOrders, order_id);
        } else {
          statusOrdersAdapter.updateOne(state.statusOrders, {
            id: order_id,
            changes: { status_number: new_status },
          });
        }
      }
    },
    saveOrderEvents: (state, action) => {
      orderEventsAdapter.upsertMany(state.orderEvents, action.payload);
    },
    updateOrderEventField(state, action) {
      const { id, changes } = action.payload;
      orderEventsAdapter.updateOne(state.orderEvents, { id, changes });
    },
    setLoadingOrders: (state, action) => {
      state.loadingOrders = action.payload;
    },
    setLoadingOrderDetail: (state, action) => {
      state.loadingOrderDetail = action.payload;
    },
    setShowOrderInfosModal: (state, action) => {
      state.orderInfosModalOrderId = action.payload.orderId;
      state.showOrderInfosModal = !!action.payload.orderId;

      if (state.showOrderInfosModal) {
        state.orderDetails = null;
      }
    },
    setShowNewOrderModal: (state, action) => {
      state.newOrderTable = action.payload.table || undefined;
      state.showNewOrderModal = action.payload.show;
    },
    setShowResetCountModal: (state, action) => {
      state.showResetCountModal = action.payload;
    },
    resetOrdersSlice: (state, action) => {
      state.orders = ordersAdapter.getInitialState();
      state.statusOrders = statusOrdersAdapter.getInitialState();
      state.orderEvents = orderEventsAdapter.getInitialState();
      state.page = 1;
      state.loadingOrders = false;
      state.showButtonMore = true;
      state.orderInfosModalOrderId = undefined;
      state.showOrderInfosModal = false;
      state.newOrderTable = undefined;
      state.showNewOrderModal = false;
      state.showResetCountModal = false;
      state.shouldPlayAlertSound = false;
      state.loadingOrderDetail = false;
      state.orderDetails = false;
      state.orderPrintDetails = false;
      state.showDispatchOrdersModal = false;
      state.ordersToDispatch = [];
    },
    setShowDispatchOrdersModal: (state, action) => {
      const { show, withProviders } = action.payload;
      state.showDispatchOrdersModal = show;
      state.showDispatchOrdersModalProviders = withProviders;
    },
    updateOrdersToDispatch(state, action) {
      const { payload } = action;
      if (Array.isArray(payload) && payload.length === 0) {
        // Reset ordersToDispatch if payload is empty
        state.ordersToDispatch = [];
        // Update all orders to set selectedToDispatch to false
        Object.values(state.orders.entities).forEach((order) => {
          if (order.selectedToDispatch) {
            ordersAdapter.updateOne(state.orders, {
              id: order.order_id,
              changes: { selectedToDispatch: false },
            });
          }
        });
      } else {
        state.ordersToDispatch = payload;
      }
    },
    addOrderToDispatch(state, action) {
      const orderId = action.payload;
      if (!state.ordersToDispatch.includes(orderId)) {
        state.ordersToDispatch.push(orderId);
        ordersAdapter.updateOne(state.orders, {
          id: orderId,
          changes: { selectedToDispatch: true },
        });
      } else {
        state.ordersToDispatch = state.ordersToDispatch.filter(
          (id) => id !== orderId
        );
        ordersAdapter.updateOne(state.orders, {
          id: orderId,
          changes: { selectedToDispatch: false },
        });
      }
    },
    addOrdersToDispatch(state, action) {
      // Extract array of order IDs from payload
      const orderItems = action.payload;

      state.ordersToDispatch = [];

      // Process each order in the payload
      orderItems.forEach((item) => {
        const orderId = item.order_id;

        // Check if this order is already in the dispatch list
        const isAlreadySelected = state.ordersToDispatch.includes(orderId);

        if (!isAlreadySelected) {
          // Add order to dispatch list
          state.ordersToDispatch.push(orderId);

          // Update order status in adapter
          ordersAdapter.updateOne(state.orders, {
            id: orderId,
            changes: { selectedToDispatch: true },
          });
        }
      });
    },
    removeOrderToDispatch(state, action) {
      const orderId = action.payload;
      state.ordersToDispatch = state.ordersToDispatch.filter(
        (id) => id !== orderId
      );
      ordersAdapter.updateOne(state.orders, {
        id: orderId,
        changes: { selectedToDispatch: false },
      });
    },
    clearOrderToDispatch(state, action) {
      const orderId = action.payload;
      state.ordersToDispatch = state.ordersToDispatch.filter(
        (id) => id !== orderId
      );
      ordersAdapter.updateOne(state.orders, {
        id: orderId,
        changes: { selectedToDispatch: false },
      });
    },
    // Overwrites only the fields derived from an order's child rows (payments,
    // products, extra deliveries). The `orders_created` socket payload is built
    // by an AFTER INSERT trigger on `orders`, which fires before those rows
    // exist, so it always carries has_card/has_drink = false and no products.
    // Local state (selectedToDispatch, status) is left untouched.
    refreshOrderDerivedFields(state, action) {
      const order = action.payload;
      if (!order?.order_id || !state.orders.entities[order.order_id]) return;
      ordersAdapter.updateOne(state.orders, {
        id: order.order_id,
        changes: {
          has_card: order.has_card,
          has_drink: order.has_drink,
          has_extra_delivery: order.has_extra_delivery,
          order_products: order.order_products,
        },
      });
    },
    setShouldPlayFirstOrderOfDayAlertSound(state, action) {
      state.shouldPlayFirstOrderOfDayAlertSound = action.payload;
    },
    addCancelledOrderToCache: (state, action) => {
      state.cancelledOrdersAlertCache[action.payload] = Date.now();
    },
    cleanCancelledOrderCache: (state) => {
      const sixMinutesAgo = Date.now() - 6 * 60 * 1000;

      if (!state.cancelledOrdersAlertCache) return; // Safeguard for undefined/null

      Object.keys(state.cancelledOrdersAlertCache).forEach((orderId) => {
        if (state.cancelledOrdersAlertCache[orderId] < sixMinutesAgo) {
          delete state.cancelledOrdersAlertCache[orderId];
        }
      });
    },
    updateOrderStatuses: (state) => {
      const allOrders = ordersAdapter.getSelectors().selectAll(state.orders);
      const updatedOrders = processOrders(allOrders);
      ordersAdapter.setAll(state.orders, updatedOrders);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addCreatedOrders.fulfilled, (state, action) => {
        const { orders } = action.payload;
        ordersAdapter.addMany(state.orders, orders); // Only add orders here
      })
      .addCase(getOrdersHistoryThunk.pending, (state) => {
        state.loadingOrders = true;
      })
      .addCase(getOrdersHistoryThunk.fulfilled, (state, action) => {
        if (action.payload.newSearch) {
          ordersAdapter.setAll(state.orders, action.payload.orders);
        } else {
          ordersAdapter.addMany(state.orders, action.payload.orders);
        }

        state.page = action.payload.page;
        state.showButtonMore = action.payload.showButtonMore;
        state.loadingOrders = false;
      })
      .addCase(updateOrders.pending, (state, action) => {
        state.statusOrders = statusOrdersAdapter.getInitialState();
        state.loadingOrders = action.meta?.arg?.newSearch ?? true;
      })
      .addCase(updateOrders.fulfilled, (state, action) => {
        if (!action.payload) return;

        // Define sorting logic
        const sortedOrders = action.payload.orders.sort((a, b) => {
          return (
            new Date(b?.preparation_start) - new Date(a?.preparation_start)
          );
        });

        // Update the orders state with sorted orders
        ordersAdapter.setAll(state.orders, sortedOrders);

        // Update the statusOrders state with the same sorted orders
        statusOrdersAdapter.setAll(state.statusOrders, sortedOrders);

        state.page = action.payload.page;
        state.showButtonMore = action.payload.showButtonMore;
        state.shouldPlayAlertSound = action.payload.shouldPlayAlertSound;
        state.loadingOrders = false;
        state.ordersToDispatch = action.payload.ordersToDispatch;
      })
      .addCase(updateStatusOrders.fulfilled, (state, action) => {
        statusOrdersAdapter.setAll(
          state.statusOrders,
          action.payload.statusOrders
        );

        state.loadingOrders = false;
      })
      .addCase(updatedOrders.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { updatedOrders } = action.payload;

        // Filter only orders with status_number < 2
        const filteredOrders = updatedOrders.filter(
          (order) => order.status_number < 2
        );

        ordersAdapter.upsertMany(state.orders, filteredOrders);

        const newOrderIds = filteredOrders.map((order) => order.order_id);

        state.orders.ids = [
          ...newOrderIds,
          ...state.orders.ids.filter((id) => !newOrderIds.includes(id)),
        ];

        if (state.orders.ids.length > 45) {
          state.orders.ids = state.orders.ids.slice(0, 45);
        }

        state.shouldPlayAlertSound = action.payload.shouldPlayAlertSound;
      })
      .addCase(searchForOrder.fulfilled, (state, action) => {
        if (!action.payload) return;

        console.log("action.payload.alignment", action.payload.alignment);
        if (action.payload.alignment === "orders-on-way") {
          console.log("orders-on-way", action.payload.orders);
          ordersAdapter.setAll(state.statusOrders, action.payload.orders);
        } else {
          console.log("NOT orders-on-way");
          ordersAdapter.setAll(state.orders, action.payload.orders);
        }
      })
      .addCase(acceptOrderThunk.fulfilled, (state, action) => {
        if (!action.payload) return;

        if (!action.payload.success) {
          return;
        }

        const { isAutoAccept } = action.payload;

        ordersAdapter.updateOne(state.orders, action.payload);

        state.shouldPlayAlertSound = Object.values(state.orders.entities).some(
          (order) => order.status_number === 0
        );

        if (!isAutoAccept) {
          state.orderInfosModalOrderId = action.payload.id;
          state.showOrderInfosModal = true;
        }
      })
      .addCase(dispatchOrders.fulfilled, (state, action) => {
        const { ordersToDispatch } = action.payload;

        // Remove the dispatched orders from the adapter
        ordersAdapter.removeMany(state.orders, ordersToDispatch);

        // Clear the ordersToDispatch array
        state.ordersToDispatch = [];
      })
      .addCase(dispatchOrders.rejected, (state, action) => {
        const data = action.payload;
        const isLimitReached =
          data?.error_code === "subscription_limit_reached" ||
          data?.details?.resource_type === "order";

        if (isLimitReached) {
          // Plan quota exceeded — show the upgrade modal (with pay link) instead
          // of a generic snackbar.
          state.orderLimitModal = {
            open: true,
            message: data.message || data.error,
            details: data.details || null,
          };
        } else if (data?.error_code?.startsWith?.("equipment_")) {
          // Shown inline on the maquininha field by SetOrderDriverModal.
        } else {
          dispatchSnackBarMessage(
            `dispatch-orders-${Date.now()}`,
            "Erro ao despachar pedidos",
            "error"
          );
        }
      })
      .addCase(postOrderDriverRequestThunk.fulfilled, (state, action) => {
        if (!action.payload) return;
        ordersAdapter.updateOne(state.orders, action.payload);
      })
      .addCase(cancelDriverRequestThunk.fulfilled, (state, action) => {
        if (!action.payload) return;
        ordersAdapter.updateOne(state.orders, action.payload);
      })
      .addCase(readyToPickupOrderThunk.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }

        if (!action.payload.success) {
          return;
        }

        ordersAdapter.updateOne(state.orders, action.payload);
      })
      .addCase(finishOrderThunk.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }

        if (action.payload.success) {
          return;
        }

        ordersAdapter.updateOne(state.orders, {
          id: action.payload.id,
          changes: action.payload.changes,
        });
      })
      .addCase(packedOrderThunk.fulfilled, (state, action) => {
        if (!action.payload) return;
        ordersAdapter.updateOne(state.orders, action.payload);
      })
      .addCase(cancelOrderThunk.fulfilled, (state, action) => {
        if (!action.payload) return;
        ordersAdapter.updateOne(state.orders, action.payload);
      })
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loadingOrderDetail = true;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.orderDetails = action.payload;
        state.loadingOrderDetail = false;
      })
      .addCase(fetchOrderPrintDetails.fulfilled, (state, action) => {
        state.orderPrintDetails = action.payload;
      })
      .addCase(setAutoAcceptOrders.fulfilled, (state, action) => {
        state.autoAcceptOrders = action.payload;
      })
      .addCase(RESET, () => initialState)
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, action) => {
          state.error = action.error.message;
        }
      );
  },
});

export const { selectAll: selectAllOrders, selectById: selectOrderById } =
  ordersAdapter.getSelectors((state) => state.orders.orders);

export const {
  selectAll: selectAllStatusOrders,
  selectById: selectStatusOrderById,
} = statusOrdersAdapter.getSelectors((state) => state.orders.statusOrders);

export const selectDeliveryOrders = createSelector(
  [selectAllOrders],
  (orders) => orders.filter((order) => order.order_type === "DELIVERY")
);

/**
 * Orders that are eligible for a pin on the dispatch map: delivery orders in a
 * mappable status with usable coordinates.
 *
 * This is the single point where the map's visible set is assembled. Orders get
 * here from three independent producers:
 *   1. the REST list (`updateOrders` -> GET /orders/v2/),
 *   2. the `orders_created` socket event (`addCreatedOrders`),
 *   3. the `order_update` socket event, which only flips `status_number` on an
 *      order already in the store — no payload, no server involvement.
 * Because of (3) no server can know which orders are currently on the map, so
 * the plan cap is applied here rather than in a serializer or the Lambda.
 */
export const selectMappableOrders = createSelector(
  [selectDeliveryOrders],
  (orders) =>
    orders.filter(
      (order) =>
        (order.status_number === 2 || order.status_number === 7) &&
        order.LAT != null &&
        order.LONG != null &&
        Number(order.LAT) !== 0 &&
        Number(order.LONG) !== 0
    )
);

const toTimestamp = (order) => {
  const time = new Date(order?.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/**
 * The orders the map actually draws, capped by the plan's `max_map_orders`.
 *
 * Oldest first by `created_at` (present on both the REST payload and the
 * socket payload), so pins stay put as new orders arrive instead of reshuffling,
 * and the orders closest to being dispatched are the ones kept.
 */
export const selectMapVisibleOrders = createSelector(
  [selectMappableOrders, (state) => state.map.orderLimit],
  (orders, limit) => {
    if (!limit || limit < 0 || orders.length <= limit) return orders;

    return [...orders].sort((a, b) => toTimestamp(a) - toTimestamp(b))
      .slice(0, limit);
  }
);

/** How many mappable orders the plan cap is currently hiding (0 when uncapped). */
export const selectMapHiddenOrdersCount = createSelector(
  [selectMappableOrders, (state) => state.map.orderLimit],
  (orders, limit) => {
    if (!limit || limit < 0) return 0;
    return Math.max(orders.length - limit, 0);
  }
);

export const selectUnpackedOrders = createSelector(
  [selectAllOrders],
  (orders) => orders.filter((order) => order.status_number === 2)
);

export const selectFirstOrderIndex = createSelector(
  [selectAllOrders],
  (orders) => {
    if (orders.length > 0) {
      return orders[0].order_index; // Return the order_index of the first order
    }
    return null; // Return null if no orders exist
  }
);

export const selectOrdersByOrdersToDispatch = createSelector(
  [selectAllOrders, (state) => state.orders.ordersToDispatch || []], // Default to an empty array if undefined
  (orders, ordersToDispatch) =>
    orders.filter((order) => ordersToDispatch.includes(order.order_id))
);

export const orderEventsSelectors = orderEventsAdapter.getSelectors(
  (state) => state.orders.orderEvents
);

export const selectFirstOrderEvent = createSelector(
  orderEventsSelectors.selectAll,
  (orderEvents) => orderEvents[0] // Return the first element
);

export const {
  updateOrderStatus,
  saveOrderEvents,
  updateOrderEventField,
  setLoadingOrderDetail,
  setLoadingOrders,
  setShowOrderInfosModal,
  setShowNewOrderModal,
  setShowResetCountModal,
  resetOrdersSlice,
  setShowDispatchOrdersModal,
  updateOrdersToDispatch,
  addOrderToDispatch,
  addOrdersToDispatch,
  removeOrderToDispatch,
  refreshOrderDerivedFields,
  setShouldPlayFirstOrderOfDayAlertSound,
  addCancelledOrderToCache,
  cleanCancelledOrderCache,
  updateOrderStatuses,
  closeOrderLimitModal,
} = ordersSlice.actions;
export default ordersSlice.reducer;
